import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { selectStore } from "../../../store/selectors/StoreSelector";
import WSHR from "../../../services/remote/hr/WSHR";
import { WSUsersInstance } from "../../../services/remote/users/WSUsers";
import { useEffect, useState } from "react";
import {
  Contract,
  ContractType,
  ContractStatus,
  ContractPaymentMethod,
  ContractPlan,
} from "../../../entity/Contract";
import { User, UserRole } from "../../../entity/User";
import "./MemberStyle.css";

// ── Label maps ────────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.ADMIN]: "Admin",
  [UserRole.SUBADMIN]: "Sub-admin",
  [UserRole.USER]: "User",
  [UserRole.STORE_EDITOR]: "Store Editor",
  [UserRole.STORE_ADMIN]: "Store Admin",
  [UserRole.STORE_CONFIRMER]: "Store Confirmer",
};

const CONTRACT_TYPE_LABELS: Record<ContractType, string> = {
  [ContractType.CDI]: "CDI",
  [ContractType.CDD]: "CDD",
  [ContractType.INTERIM]: "Interim",
  [ContractType.FREELANCE]: "Freelance",
};

const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  [ContractStatus.ACTIVE]: "Active",
  [ContractStatus.INACTIVE]: "Inactive",
  [ContractStatus.EXPIRED]: "Expired",
};

const PAYMENT_METHOD_LABELS: Record<ContractPaymentMethod, string> = {
  [ContractPaymentMethod.CASH]: "Cash",
  [ContractPaymentMethod.BANK_TRANSFER]: "Bank Transfer",
  [ContractPaymentMethod.CREDIT_CARD]: "Credit Card",
  [ContractPaymentMethod.PAYPAL]: "PayPal",
};

const CONTRACT_PLAN_LABELS: Record<number, string> = {
  [ContractPlan.SEVEN_DAYS]: "7 days/week",
  [ContractPlan.SIX_DAYS]: "6 days/week",
  [ContractPlan.FIVE_DAYS]: "5 days/week",
  [ContractPlan.FOUR_DAYS]: "4 days/week",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (date?: Date | string) => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getInitials = (email: string) => email.slice(0, 2).toUpperCase();

const statusBadgeClass = (status?: ContractStatus) => {
  switch (status) {
    case ContractStatus.ACTIVE:
      return "ms-status-badge ms-status-badge--active";
    case ContractStatus.INACTIVE:
      return "ms-status-badge ms-status-badge--inactive";
    case ContractStatus.EXPIRED:
      return "ms-status-badge ms-status-badge--expired";
    default:
      return "ms-status-badge ms-status-badge--inactive";
  }
};

const statusDotClass = (status?: ContractStatus) => {
  switch (status) {
    case ContractStatus.ACTIVE:
      return "ms-status-dot ms-status-dot--active";
    case ContractStatus.INACTIVE:
      return "ms-status-dot ms-status-dot--inactive";
    case ContractStatus.EXPIRED:
      return "ms-status-dot ms-status-dot--expired";
    default:
      return "ms-status-dot ms-status-dot--inactive";
  }
};

// ── Types ─────────────────────────────────────────────────────────────────────

type EditContractForm = Omit<
  Contract,
  "id" | "userId" | "storeId" | "createdAt" | "updatedAt"
>;

// ── Component ─────────────────────────────────────────────────────────────────

export const MemberScreen = () => {
  const { userId } = useParams();
  const store = useSelector(selectStore);

  const [user, setUser] = useState<User | null>(null);
  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState<EditContractForm>({});

  const wsHRInstance = WSHR;
  const wsUserInstance = WSUsersInstance;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [userRes, contractRes] = await Promise.all([
        wsUserInstance.getUserById(userId || ""),
        wsHRInstance.getContractsByUserId(userId || ""),
      ]);
      setUser(userRes);
      const fetchedContract = Array.isArray(contractRes)
        ? (contractRes[0] ?? null)
        : (contractRes ?? null);
      setContract(fetchedContract);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [store]);

  const openEditModal = () => {
    if (contract) {
      setEditForm({
        plan: contract.plan,
        contractType: contract.contractType,
        status: contract.status,
        paymentMethod: contract.paymentMethod,
        startDate: contract.startDate,
        endDate: contract.endDate,
        salary: contract.salary,
      });
    } else {
      setEditForm({});
    }
    setShowEditModal(true);
  };

  const handleSave = async () => {
    if (!userId || !store?.id) return;
    setSaving(true);
    try {
      if (contract?.id) {
        await wsHRInstance.updateContract(
          contract.id,
          store.id,
          userId,
          editForm,
        );
      } else {
        await wsHRInstance.createContract({
          ...editForm,
          userId,
          storeId: store.id,
        });
      }
      await fetchData();
      setShowEditModal(false);
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="ms-page">
        <div className="ms-loading-wrap">
          <div className="ms-spinner" />
          <span className="ms-loading-text">Loading member…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="ms-page">
      {/* ── Header ── */}
      {/* <div className="ms-header">
        <button className="ms-back-btn" onClick={() => window.history.back()}>
          ← Back
        </button>
        <h1 className="ms-page-title">Member Profile</h1>
      </div> */}

      <div className="ms-content">
        {/* ── User Card ── */}
        <section className="ms-card">
          <div className="ms-user-top">
            <div className="ms-avatar">
              {user ? getInitials(user.email) : "??"}
            </div>
            <div className="ms-user-info">
              <p className="ms-user-email">{user?.email ?? "—"}</p>
              <span className="ms-role-badge">
                {user ? ROLE_LABELS[user.role] : "—"}
              </span>
            </div>
            <div
              className={
                user?.isActive
                  ? "ms-user-status-dot ms-user-status-dot--active"
                  : "ms-user-status-dot ms-user-status-dot--inactive"
              }
            />
          </div>

          <div className="ms-divider" />

          <div className="ms-field-grid">
            <InfoField label="User ID" value={user?.id ?? "—"} mono />
            <InfoField label="Store ID" value={user?.storeId ?? "—"} mono />
            <InfoField label="Joined" value={formatDate(user?.createdAt)} />
            <InfoField
              label="Last updated"
              value={formatDate(user?.updatedAt)}
            />
            <InfoField
              label="Status"
              value={
                <span
                  className={
                    user?.isActive
                      ? "ms-status-text--active"
                      : "ms-status-text--inactive"
                  }
                >
                  {user?.isActive ? "Active" : "Inactive"}
                </span>
              }
            />
          </div>

          {/* ── Contract Card ── */}
          <div className="ms-section-header">
            <h2 className="ms-section-title">Contract</h2>
            <button className="ms-edit-btn" onClick={openEditModal}>
              {contract ? "Edit contract" : "Add contract"}
            </button>
          </div>

          {contract ? (
            <>
              <div className="ms-contract-status-row">
                <span className={statusBadgeClass(contract.status)}>
                  <span className={statusDotClass(contract.status)} />
                  {CONTRACT_STATUS_LABELS[contract.status!] ?? "—"}
                </span>
                <span className="ms-contract-type-badge">
                  {contract.contractType
                    ? CONTRACT_TYPE_LABELS[contract.contractType]
                    : "—"}
                </span>
              </div>

              <div className="ms-divider" />

              <div className="ms-field-grid">
                <InfoField
                  label="Salary"
                  value={
                    contract.salary != null
                      ? `${contract.salary.toLocaleString()}`
                      : "—"
                  }
                />
                <InfoField
                  label="Plan"
                  value={
                    contract.plan != null
                      ? CONTRACT_PLAN_LABELS[contract.plan]
                      : "—"
                  }
                />
                <InfoField
                  label="Payment method"
                  value={
                    contract.paymentMethod
                      ? PAYMENT_METHOD_LABELS[contract.paymentMethod]
                      : "—"
                  }
                />
                <InfoField
                  label="Start date"
                  value={formatDate(contract.startDate)}
                />
                <InfoField
                  label="End date"
                  value={formatDate(contract.endDate)}
                />
                <InfoField label="Contract ID" value={contract.id} mono />
              </div>
            </>
          ) : (
            <div className="ms-empty-state">
              <span className="ms-empty-icon">📋</span>
              <p className="ms-empty-text">No contract assigned yet.</p>
              <p className="ms-empty-subtext">
                Click "Add contract" to create one.
              </p>
            </div>
          )}
        </section>
      </div>

      {/* ── Edit Modal ── */}
      {showEditModal && (
        <div className="ms-overlay">
          <div className="ms-modal">
            <div className="ms-modal-header">
              <h2 className="ms-modal-title">
                {contract ? "Edit contract" : "New contract"}
              </h2>
              <button
                className="ms-close-btn"
                onClick={() => setShowEditModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="ms-modal-body">
              <FormRow label="Contract type">
                <select
                  className="ms-select"
                  value={editForm.contractType ?? ""}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      contractType:
                        (e.target.value as ContractType) || undefined,
                    }))
                  }
                >
                  <option value="">Select…</option>
                  {Object.entries(ContractType).map(([, v]) => (
                    <option key={v as string} value={v as string}>
                      {CONTRACT_TYPE_LABELS[v as ContractType]}
                    </option>
                  ))}
                </select>
              </FormRow>

              <FormRow label="Status">
                <select
                  className="ms-select"
                  value={editForm.status ?? ""}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      status: (e.target.value as ContractStatus) || undefined,
                    }))
                  }
                >
                  <option value="">Select…</option>
                  {Object.entries(ContractStatus).map(([, v]) => (
                    <option key={v as string} value={v as string}>
                      {CONTRACT_STATUS_LABELS[v as ContractStatus]}
                    </option>
                  ))}
                </select>
              </FormRow>

              <FormRow label="Plan">
                <select
                  className="ms-select"
                  value={editForm.plan ?? ""}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      plan: e.target.value
                        ? (Number(e.target.value) as ContractPlan)
                        : undefined,
                    }))
                  }
                >
                  <option value="">Select…</option>
                  {Object.entries(ContractPlan)
                    .filter(([, v]) => typeof v === "number")
                    .map(([, v]) => (
                      <option key={v as number} value={v as number}>
                        {CONTRACT_PLAN_LABELS[v as number]}
                      </option>
                    ))}
                </select>
              </FormRow>

              <FormRow label="Payment method">
                <select
                  className="ms-select"
                  value={editForm.paymentMethod ?? ""}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      paymentMethod:
                        (e.target.value as ContractPaymentMethod) || undefined,
                    }))
                  }
                >
                  <option value="">Select…</option>
                  {Object.entries(ContractPaymentMethod).map(([, v]) => (
                    <option key={v as string} value={v as string}>
                      {PAYMENT_METHOD_LABELS[v as ContractPaymentMethod]}
                    </option>
                  ))}
                </select>
              </FormRow>

              <FormRow label="Salary">
                <input
                  type="number"
                  className="ms-input"
                  value={editForm.salary ?? ""}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      salary: e.target.value
                        ? Number(e.target.value)
                        : undefined,
                    }))
                  }
                  placeholder="e.g. 45000"
                />
              </FormRow>

              <div className="ms-date-row">
                <FormRow label="Start date">
                  <input
                    type="date"
                    className="ms-input"
                    value={
                      editForm.startDate
                        ? new Date(editForm.startDate)
                            .toISOString()
                            .slice(0, 10)
                        : ""
                    }
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        startDate: e.target.value
                          ? new Date(e.target.value)
                          : undefined,
                      }))
                    }
                  />
                </FormRow>
                <FormRow label="End date">
                  <input
                    type="date"
                    className="ms-input"
                    value={
                      editForm.endDate
                        ? new Date(editForm.endDate).toISOString().slice(0, 10)
                        : ""
                    }
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        endDate: e.target.value
                          ? new Date(e.target.value)
                          : undefined,
                      }))
                    }
                  />
                </FormRow>
              </div>
            </div>

            <div className="ms-modal-footer">
              <button
                className="ms-cancel-btn"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button
                className="ms-save-btn"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const InfoField = ({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) => (
  <div className="ms-info-field">
    <span className="ms-field-label">{label}</span>
    <span className={mono ? "ms-field-value--mono" : "ms-field-value"}>
      {value}
    </span>
  </div>
);

const FormRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="ms-form-row">
    <label className="ms-form-label">{label}</label>
    {children}
  </div>
);
